const RECENTLY_VIEWED_KEY = 'hallowviaRecentlyViewed.v1';
const RECENT_CARD_CACHE_PREFIX = 'hallowviaRecentCard.v1';
const MAX_STORED_PRODUCTS = 12;
const MAX_VISIBLE_PRODUCTS = 4;
const MOBILE_PRODUCT_CARD_QUERY = '(max-width: 749px)';
const MOBILE_PRODUCT_CARD_TAP_TOLERANCE = 12;
const sizeGuideReturnFocus = new WeakMap();
const mobileProductCardPresses = new Map();
const SIZE_GUIDE_MEASUREMENT_KEYS = ['shoulder', 'chest', 'waist', 'length'];
const CHILD_COSTUME_PATTERN = /\b(?:kids?|children?|child|girls?|boys?|enfants?)\b/i;
const MANUFACTURER_SIZE_NOTICE_PATTERN = /(?:size (?:printed )?on the costume label|taille (?:imprimée|inscrite|indiquée) sur l[’']étiquette|talla (?:impresa|indicada) en la etiqueta)/i;
const CHILD_MANUFACTURER_SIZE_NOTICES = {
  en: 'Please note: the size printed on the costume label may differ from the size selected on Hallowvia because it reflects the manufacturer’s sizing. Please rely on the measurements in the size chart.',
  fr: 'Veuillez noter que la taille imprimée sur l’étiquette du costume peut différer de la taille sélectionnée sur Hallowvia, car elle correspond au barème du fabricant. Fiez-vous aux mesures du tableau des tailles.',
  es: 'Tenga en cuenta que la talla impresa en la etiqueta del disfraz puede diferir de la talla seleccionada en Hallowvia, porque corresponde al tallaje del fabricante. Consulte las medidas de la tabla de tallas.',
};
const PRODUCT_DETAIL_LABEL_SELECTOR = [
  '.product-information--hallowvia .product-information__additional-blocks .accordion > accordion-custom:first-of-type .metafield-rich_text_field li > strong:first-child',
  '.product-information--hallowvia .product-information__additional-blocks .accordion > accordion-custom:first-of-type .metafield-rich_text_field li > b:first-child',
].join(',');
const PRODUCT_DETAIL_SEPARATOR = '\u00a0:\u00a0';

function normalizeProductDetailLabel(value) {
  return String(value || '').trim().replace(/\s*:\s*$/, '');
}

function normalizeProductDetailLabels(root = document) {
  root.querySelectorAll(PRODUCT_DETAIL_LABEL_SELECTOR).forEach((label) => {
    if (!(label instanceof HTMLElement) || label.dataset.hallowviaPunctuationNormalized === 'true') return;

    const normalizedLabel = normalizeProductDetailLabel(label.textContent);
    if (!normalizedLabel) return;

    label.textContent = normalizedLabel;

    const followingText = label.nextSibling;
    if (followingText?.nodeType === Node.TEXT_NODE) {
      followingText.textContent = followingText.textContent.replace(/^\s*:?\s*/, '');
    }

    label.insertAdjacentText('afterend', PRODUCT_DETAIL_SEPARATOR);
    label.dataset.hallowviaPunctuationNormalized = 'true';
  });
}

function readStorage(key, fallback = []) {
  try {
    const value = JSON.parse(localStorage.getItem(key) || 'null');
    return Array.isArray(value) ? value : fallback;
  } catch (_error) {
    return fallback;
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_error) {
    // Storage can be unavailable in privacy modes. The page remains fully usable.
  }
}

function readRecentCardCache(key) {
  try {
    return sessionStorage.getItem(key) || '';
  } catch (_error) {
    return '';
  }
}

function writeRecentCardCache(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (_error) {
    return;
  }
}

function validProduct(product) {
  return (
    product &&
    String(product.id || '').length > 0 &&
    typeof product.url === 'string' &&
    typeof product.title === 'string'
  );
}

function productFromDataset(card) {
  return {
    id: card.dataset.productId,
    handle: card.dataset.productHandle || '',
    url: card.dataset.productUrl || '',
    title: card.dataset.productTitle || '',
    price: card.dataset.productPrice || '',
    image: card.dataset.productImage || '',
    badge: card.dataset.productBadge || '',
  };
}

function productHandle(product) {
  if (typeof product.handle === 'string' && product.handle.trim()) return product.handle.trim();
  try {
    const pathname = new URL(product.url, window.location.origin).pathname;
    const match = pathname.match(/\/products\/([^/]+)\/?$/);
    return match ? decodeURIComponent(match[1]) : '';
  } catch (_error) {
    return '';
  }
}

function createRecentCard(product) {
  const item = document.createElement('article');
  item.className = 'hallowvia-recent-card';

  const link = document.createElement('a');
  link.className = 'hallowvia-recent-card__link';
  link.href = product.url;
  link.setAttribute('aria-label', product.title);

  const media = document.createElement('span');
  media.className = 'hallowvia-recent-card__media';

  const mediaLink = document.createElement('a');
  mediaLink.className = 'hallowvia-recent-card__media-link';
  mediaLink.href = product.url;
  mediaLink.setAttribute('aria-label', product.title);

  if (product.image) {
    const image = document.createElement('img');
    image.src = product.image;
    image.alt = product.title;
    image.loading = 'lazy';
    image.width = 720;
    image.height = 900;
    mediaLink.append(image);
  }

  media.append(mediaLink);

  if (product.badge) {
    const badge = document.createElement('span');
    badge.className = 'hallowvia-recent-card__badge';
    badge.textContent = product.badge;
    media.append(badge);
  }

  const title = document.createElement('span');
  title.className = 'hallowvia-recent-card__title';
  title.textContent = product.title;

  const price = document.createElement('span');
  price.className = 'hallowvia-recent-card__price';
  price.textContent = product.price;

  const priceRow = document.createElement('span');
  priceRow.className = 'hallowvia-recent-card__price-row hallowvia-card-price-row';
  priceRow.append(price);

  const favorite = document.createElement('button');
  favorite.className = 'hallowvia-card__favorite hallowvia-recent-card__favorite';
  favorite.type = 'button';
  favorite.dataset.hallowviaFavorite = '';
  favorite.dataset.productHandle = product.handle;
  favorite.dataset.productId = product.id;
  favorite.dataset.addLabel = document.documentElement.lang === 'fr' ? 'Ajouter aux favoris' : 'Add to favorites';
  favorite.dataset.removeLabel = document.documentElement.lang === 'fr' ? 'Retirer des favoris' : 'Remove from favorites';
  favorite.setAttribute('aria-label', favorite.dataset.addLabel);
  favorite.setAttribute('aria-pressed', 'false');
  favorite.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.5 9c0 5-8.5 10-8.5 10S3.5 14 3.5 9A4.5 4.5 0 0 1 12 6.8 4.5 4.5 0 0 1 20.5 9Z"/></svg>';

  link.append(title, priceRow);
  item.append(media, link, favorite);
  return item;
}

async function hydratedRecentCard(product, rootUrl) {
  const handle = productHandle(product);
  if (!handle) return createRecentCard(product);

  const cacheKey = `${RECENT_CARD_CACHE_PREFIX}:${document.documentElement.lang || 'en'}:${rootUrl}:${handle}`;
  const cachedMarkup = readRecentCardCache(cacheKey);
  if (cachedMarkup) {
    const cachedDocument = new DOMParser().parseFromString(cachedMarkup, 'text/html');
    const cachedCard = cachedDocument.querySelector('[data-hallowvia-recent-card]');
    if (cachedCard) return cachedCard;
  }

  try {
    const response = await fetch(
      `${rootUrl}products/${encodeURIComponent(handle)}?section_id=hallowvia-recent-product-card`,
      { headers: { Accept: 'text/html' } }
    );
    if (!response.ok) return createRecentCard(product);
    const responseMarkup = await response.text();
    writeRecentCardCache(cacheKey, responseMarkup);
    const responseDocument = new DOMParser().parseFromString(responseMarkup, 'text/html');
    return responseDocument.querySelector('[data-hallowvia-recent-card]') || createRecentCard(product);
  } catch (_error) {
    return createRecentCard(product);
  }
}

function hydrateRecentlyViewedCards(section, grid, visibleProducts, rootUrl) {
  if (section.dataset.hallowviaRecentHydrationStarted === 'true') return;
  section.dataset.hallowviaRecentHydrationStarted = 'true';
  grid.setAttribute('aria-busy', 'true');

  Promise.all(visibleProducts.map((recentProduct) => hydratedRecentCard(recentProduct, rootUrl)))
    .then((cards) => {
      grid.replaceChildren(...cards);
      window.HallowviaFavorites?.sync(grid);
    })
    .finally(() => grid.removeAttribute('aria-busy'));
}

function scheduleRecentlyViewedHydration(section, grid, visibleProducts, rootUrl) {
  const hydrate = () => hydrateRecentlyViewedCards(section, grid, visibleProducts, rootUrl);

  if (!('IntersectionObserver' in window)) {
    hydrate();
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      hydrate();
    },
    { rootMargin: '700px 0px' }
  );
  observer.observe(section);
}

async function initializeRecentlyViewed(section) {
  const currentScript = section.querySelector('[data-hallowvia-current-product]');
  const grid = section.querySelector('[data-hallowvia-recent-grid]');
  if (!currentScript || !grid) return;
  const rootUrl = `${section.dataset.rootUrl || ''}/`.replace(/\/{2,}/g, '/');

  let currentProduct;
  try {
    currentProduct = JSON.parse(currentScript.textContent || '{}');
  } catch (_error) {
    return;
  }
  if (!validProduct(currentProduct)) return;

  const fallbackProducts = Array.from(grid.querySelectorAll('[data-fallback-product]'))
    .map(productFromDataset)
    .filter(validProduct);
  const storedProducts = readStorage(RECENTLY_VIEWED_KEY).filter(validProduct);
  const visibleProducts = [];
  const usedIds = new Set([String(currentProduct.id)]);

  for (const candidate of [...storedProducts, ...fallbackProducts]) {
    const id = String(candidate.id);
    if (usedIds.has(id)) continue;
    usedIds.add(id);
    visibleProducts.push(candidate);
    if (visibleProducts.length === MAX_VISIBLE_PRODUCTS) break;
  }

  const nextHistory = [currentProduct, ...storedProducts]
    .filter((candidate, index, products) => {
      const id = String(candidate.id);
      return index === products.findIndex((product) => String(product.id) === id);
    })
    .slice(0, MAX_STORED_PRODUCTS);
  writeStorage(RECENTLY_VIEWED_KEY, nextHistory);

  section.hidden = visibleProducts.length === 0;
  if (visibleProducts.length === 0) {
    grid.replaceChildren();
    return;
  }

  scheduleRecentlyViewedHydration(section, grid, visibleProducts, rootUrl);
}

function closeSizeGuideModal(modal, restoreFocus = true) {
  if (!modal) return;
  modal.classList.remove('is-open');
  modal.style.display = 'none';
  modal.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.remove('hallowvia-size-guide-open');

  const shell = modal.closest('dialog.hallowvia-size-guide-dialog-shell');
  if (shell instanceof HTMLDialogElement && shell.open) shell.close();

  const returnFocus = sizeGuideReturnFocus.get(modal);
  if (restoreFocus && returnFocus instanceof HTMLElement) returnFocus.focus();
}

function openSizeGuideModal(modal, trigger) {
  if (!modal) return;
  initializeSizeGuideModal(modal);
  sizeGuideReturnFocus.set(modal, trigger);

  const shell = modal.closest('dialog.hallowvia-size-guide-dialog-shell');
  if (shell instanceof HTMLDialogElement && !shell.open) shell.showModal();

  modal.style.display = 'flex';
  modal.classList.add('is-open');
  modal.setAttribute('aria-hidden', 'false');
  document.documentElement.classList.add('hallowvia-size-guide-open');
  modal.querySelector('.hallowvia-size-guide-body')?.scrollTo({ top: 0, left: 0 });
  modal.querySelector('.hallowvia-size-guide-close')?.focus();
}

function formatSizeGuideNote(panel) {
  const tableScroll = panel.querySelector('.size-guide-table-scroll');
  const sourceNote = tableScroll?.nextElementSibling;
  if (!(sourceNote instanceof HTMLParagraphElement)) return;

  let noteText = sourceNote.textContent.trim();
  if (!noteText) return;

  noteText = noteText.replace(
    /(maximum comfort|confort maximal)([.!?]?)(?=\s+\S)/i,
    '$1$2\n'
  );

  const paragraphs = noteText
    .split(/\r?\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  if (paragraphs.length === 0) return;

  const note = document.createElement('div');
  note.className = 'hallowvia-size-guide-note';
  paragraphs.forEach((paragraph) => {
    const text = document.createElement('p');
    text.textContent = paragraph;
    note.append(text);
  });
  sourceNote.replaceWith(note);
}

function formatSizeGuideMeasurementHeading(value, labels) {
  const match = String(value || '').match(/^\s*([1-4])\s*[—–-]\s*/);
  if (!match) return value;
  const label = labels[Number(match[1]) - 1];
  return label ? `${match[1]} — ${label}` : value;
}

function localizeSizeGuideMeasurements(panel) {
  const details = panel.querySelector(':scope > details');
  if (!details) return;

  const labels = SIZE_GUIDE_MEASUREMENT_KEYS.map((key) => {
    const datasetKey = `sizeGuide${key.charAt(0).toUpperCase()}${key.slice(1)}`;
    return document.documentElement.dataset[datasetKey] || '';
  });

  details.querySelectorAll('strong').forEach((heading) => {
    heading.textContent = formatSizeGuideMeasurementHeading(heading.textContent, labels);
  });

  const diagramAlt = document.documentElement.dataset.sizeGuideDiagramAlt;
  if (diagramAlt) details.querySelector('svg')?.setAttribute('aria-label', diagramAlt);
}

function isChildCostumeDescriptor(value) {
  return CHILD_COSTUME_PATTERN.test(String(value || ''));
}

function hasManufacturerSizeNotice(value) {
  return MANUFACTURER_SIZE_NOTICE_PATTERN.test(String(value || ''));
}

function ensureChildSizeGuideNotice(modal, panel) {
  const productId = modal.id.replace(/^size-guide-/, '');
  const ownerSection = modal.closest('.shopify-section');
  const picker = ownerSection?.querySelector(`variant-picker[data-product-id="${productId}"]`);
  const descriptor = `${picker?.dataset.productTitle || ''} ${picker?.dataset.productType || ''}`;

  if (!isChildCostumeDescriptor(descriptor) || hasManufacturerSizeNotice(panel.textContent)) return;

  let note = panel.querySelector(':scope > .hallowvia-size-guide-note');
  if (!note) {
    note = document.createElement('div');
    note.className = 'hallowvia-size-guide-note';
    const details = Array.from(panel.children).find((element) => element.tagName === 'DETAILS');
    panel.insertBefore(note, details || null);
  }

  const notice = document.createElement('p');
  const locale = document.documentElement.lang?.toLowerCase().split('-')[0];
  notice.textContent = CHILD_MANUFACTURER_SIZE_NOTICES[locale] || CHILD_MANUFACTURER_SIZE_NOTICES.en;
  note.append(notice);
}

function initializeSizeGuideModal(modal) {
  if (modal.dataset.hallowviaModalInitialized === 'true') return;

  const ownerSection = modal.closest('.shopify-section');
  const legacyTrigger = modal.previousElementSibling;
  const panel = modal.firstElementChild;
  if (!panel) return;
  const closeButton = panel.querySelector(':scope > button');
  const header = panel.querySelector(':scope > div');
  const heading = header?.querySelector('h3');

  formatSizeGuideNote(panel);
  localizeSizeGuideMeasurements(panel);
  ensureChildSizeGuideNotice(modal, panel);

  if (legacyTrigger instanceof HTMLButtonElement) {
    legacyTrigger.classList.add('hallowvia-size-guide-legacy-trigger');
    legacyTrigger.hidden = true;
    legacyTrigger.remove();
  }

  modal.classList.add('hallowvia-size-guide-overlay');
  modal.setAttribute('aria-hidden', 'true');
  panel.classList.add('hallowvia-size-guide-panel');
  closeButton?.classList.add('hallowvia-size-guide-close');
  header?.classList.add('hallowvia-size-guide-header');
  if (header && closeButton) header.append(closeButton);
  if (heading) {
    heading.id ||= `${modal.id}-title`;
  }

  if (!panel.querySelector(':scope > .hallowvia-size-guide-body')) {
    const body = document.createElement('div');
    body.className = 'hallowvia-size-guide-body';
    const content = Array.from(panel.children).filter(
      (element) => element !== closeButton && element !== header
    );
    body.append(...content);
    panel.append(body);
  }

  modal.dataset.hallowviaOwnerSection = ownerSection?.id || '';

  const shell = document.createElement('dialog');
  shell.className = 'hallowvia-size-guide-dialog-shell';
  if (heading?.id) {
    shell.setAttribute('aria-labelledby', heading.id);
  } else {
    shell.setAttribute('aria-label', 'Size guide');
  }
  shell.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeSizeGuideModal(modal);
  });
  document.body.append(shell);
  shell.append(modal);

  closeButton?.addEventListener('click', () => closeSizeGuideModal(modal));
  modal.addEventListener('click', (event) => {
    if (event.target === modal) closeSizeGuideModal(modal);
  });
  modal.dataset.hallowviaModalInitialized = 'true';
}

function initializeSizeGuideLink(button) {
  if (button.dataset.hallowviaLinkInitialized === 'true') return;
  button.addEventListener('click', () => {
    const modalId = button.getAttribute('aria-controls');
    const modal = modalId ? document.getElementById(modalId) : null;
    openSizeGuideModal(modal, button);
  });
  button.dataset.hallowviaLinkInitialized = 'true';
}

function handleDynamicSizeGuideLink(event) {
  const button = event.target.closest?.('[data-hallowvia-size-guide-link]');
  if (!button || button.dataset.hallowviaLinkInitialized === 'true') return;

  event.preventDefault();
  const modalId = button.getAttribute('aria-controls');
  const modal = modalId ? document.getElementById(modalId) : null;
  openSizeGuideModal(modal, button);
}

function initializeLocalization(select) {
  select.addEventListener('change', () => select.form?.requestSubmit());
}

function mobileProductCardLink(target) {
  if (!(target instanceof Element)) return null;
  if (target.closest('button, input, label, select, textarea')) return null;

  const recentMedia = target.closest('.hallowvia-recent-card__media');
  const recentCard = recentMedia?.closest('.hallowvia-recent-card');
  if (recentCard?.closest('[data-hallowvia-recently-viewed]')) {
    return recentCard.querySelector('.hallowvia-recent-card__link[href]');
  }

  const recommendationGallery = target.closest('.card-gallery');
  const recommendationCard = recommendationGallery?.closest('product-card');
  if (recommendationCard?.closest('product-recommendations.product-recommendations--hallowvia')) {
    return recommendationCard.querySelector('[ref="productCardLink"][href], .card-gallery a[href]');
  }

  return null;
}

function isMobileProductCardTap(event) {
  return (
    event.isPrimary !== false &&
    event.button === 0 &&
    window.matchMedia(MOBILE_PRODUCT_CARD_QUERY).matches
  );
}

function handleMobileProductCardPointerDown(event) {
  if (!isMobileProductCardTap(event)) return;
  const link = mobileProductCardLink(event.target);
  if (!(link instanceof HTMLAnchorElement) || !link.href) return;

  mobileProductCardPresses.set(event.pointerId, {
    x: event.clientX,
    y: event.clientY,
    href: link.href,
  });
}

function handleMobileProductCardPointerUp(event) {
  const press = mobileProductCardPresses.get(event.pointerId);
  mobileProductCardPresses.delete(event.pointerId);
  if (!press || !isMobileProductCardTap(event)) return;

  const distance = Math.hypot(event.clientX - press.x, event.clientY - press.y);
  if (distance > MOBILE_PRODUCT_CARD_TAP_TOLERANCE) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  window.location.assign(press.href);
}

function cancelMobileProductCardPress(event) {
  mobileProductCardPresses.delete(event.pointerId);
}

function initializeHallowviaProductPage(root = document) {
  normalizeProductDetailLabels(root);
  root.querySelectorAll('[id^="size-guide-"]').forEach(initializeSizeGuideModal);
  root.querySelectorAll('[data-hallowvia-recently-viewed]').forEach(initializeRecentlyViewed);
  root.querySelectorAll('[data-hallowvia-size-guide-link]').forEach(initializeSizeGuideLink);
  root.querySelectorAll('[data-hallowvia-footer-country]').forEach(initializeLocalization);
}

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const openModal = document.querySelector('.hallowvia-size-guide-overlay.is-open');
  if (openModal) closeSizeGuideModal(openModal);
});

document.addEventListener('click', handleDynamicSizeGuideLink);

initializeHallowviaProductPage();

document.addEventListener('shopify:section:load', (event) => {
  initializeHallowviaProductPage(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  document
    .querySelectorAll('.hallowvia-size-guide-overlay')
    .forEach((modal) => {
      if (modal.dataset.hallowviaOwnerSection === event.target.id) modal.remove();
    });
});

document.addEventListener('pointerdown', handleMobileProductCardPointerDown, true);
document.addEventListener('pointerup', handleMobileProductCardPointerUp, true);
document.addEventListener('pointercancel', cancelMobileProductCardPress, true);

export {
  formatSizeGuideMeasurementHeading,
  hasManufacturerSizeNotice,
  isChildCostumeDescriptor,
  normalizeProductDetailLabel,
};
